import React, { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DynamicTransactionTable from "../../../components/tables/dynamic-transaction-table";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import { DataContext } from "../../../components/layouts/main-layout";
import moment from "moment";
import EInvoiceStatusBadge from "../../../components/sales-section/e-invoice/e-invoice-status-badge";
import { eWayBillTableAction } from "../../../redux/actions/sales-action";
import FilterRadioButtons from "../../../components/forms/filter-radio-buttons";
import CancelEWayBillModal from "../../../components/sales-section/e-way-bills/cancel-e-way-bill-modal";
import RejectEWayBillModal from "../../../components/sales-section/e-way-bills/reject-e-way-bill-modal";
import { Ban, XCircle } from "lucide-react";

const EWB_CANCEL_WINDOW_HOURS = 24;
/* NIC allows the recipient to reject an e-way bill within 72 hours of generation */
const EWB_REJECT_WINDOW_HOURS = 72;

/* EWB dates come as "2026-10-24 23:59:00" (IRN flow) or "24/10/2026 11:59:00 PM" (standalone) */
const EWB_DATE_FORMATS = [
    "YYYY-MM-DD HH:mm:ss",
    "DD/MM/YYYY hh:mm:ss A",
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY",
    moment.ISO_8601,
];

/* moment for an EWB date string, or null when it can't be read */
const parseEwbDate = (value) => {
    if (!value) return null;
    const parsed = moment(value, EWB_DATE_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

/* "ewbNo" / "ewb_no" / "EwbNo" → "ewbno" */
const normalizeKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");

/*
 * Value of the first matching key on the row or on any object nested one level
 * inside it (the EWB details come back nested in the list response).
 */
const findRowField = (item, keys) => {
    const wanted = keys.map(normalizeKey);
    const objects = [item, ...Object.values(item ?? {}).filter((v) => v && typeof v === "object" && !Array.isArray(v))];
    for (const obj of objects) {
        for (const [k, v] of Object.entries(obj)) {
            if (wanted.includes(normalizeKey(k)) && v !== null && v !== undefined && v !== "") return v;
        }
    }
    return null;
};

/* ── EWB-specific filter: only Cancelled + Rejected ── */
const EWB_STATUS_OPTIONS = [
    { label: "Cancelled", value: "cancelled" },
    { label: "Rejected",  value: "rejected"  },
];

const EWayBillFilter = ({ filterData, setFilterData, setCurrentPage }) => {
    const handleChange = (e) => {
        setFilterData({ ...filterData, orderStatus: e.target.value });
        setCurrentPage(1);
    };
    return (
        <div
            style={{
                minWidth: "13rem",
                zIndex: 999,
                top: "40px",
                right: "-5px",
                boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)",
            }}
            className="absolute border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded z-10 flex flex-col"
        >
            <span className="text-[length:--popover-heading-font-size] font-[var(--popover-heading-font-weight)] text-[var(--popover-heading-fg-color)] whitespace-nowrap px-[8px] py-[12px]">
                FILTER BY
            </span>
            <hr className="border border-EAECF0" />
            <div className="px-5">
                <FilterRadioButtons
                    name="ewb_status"
                    label="Status"
                    options={EWB_STATUS_OPTIONS}
                    handleChange={handleChange}
                    value={filterData?.orderStatus}
                />
            </div>
        </div>
    );
};

const EWayBillsTable = () => {
    const { permissionFn } = useContext(DataContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [eWayBills, setEWayBills] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [reload, setReload] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [cancelRow, setCancelRow] = useState(null);
    const [rejectRow, setRejectRow] = useState(null);

    const [filterData, setFilterData] = useState({
        sort_by: "",
        sort_order: "",
        selectColumn: "",
        from: "",
        to: "",
        inputValue: "",
        orderStatus: "",
    });

    useEffect(() => {
        dispatch(
            eWayBillTableAction(
                {
                    pageNumber: currentPage,
                    pageSize: pageSize,
                    filterData,
                },
                onSuccess,
                onError
            )
        );
    }, [currentPage, pageSize, filterData, reload]);

    const onSuccess = (data) => {
        setError(false);
        setLoading(false);
        /* handle multiple possible response shapes from the EWB list API */
        const list =
            data?.data?.list ??
            data?.data?.orderList ??
            data?.data?.ewbList ??
            (Array.isArray(data?.data) ? data.data : []);
        const count =
            data?.data?.totalCount ??
            data?.totalCount ??
            list.length;
        setEWayBills(list);
        setDataCount(count);
    };

    const onError = () => {
        setError(true);
        setLoading(false);
    };

    const paginationProps = {
        pageSize,
        setPageSize,
        dataCount,
        setCurrentPage,
        currentPage,
    };

    const handleFilterClick = useCallback(() => {
        setShowFilter((prev) => !prev);
    }, []);

    const filterby = showFilter ? (
        <EWayBillFilter
            filterData={filterData}
            setFilterData={setFilterData}
            setCurrentPage={setCurrentPage}
        />
    ) : null;

    const tableBodyData = eWayBills?.map((item) => {
        /* response may be flat or nested under sales_order / eway_bill */
        const inv  = item?.sales_order ?? item;
        const ewb  = item?.eway_bill   ?? item;

        const invNo = inv?.soit_invoice_number ?? ewb?.ewb_invoice_number ?? "--";
        const ewbStatus = item?.ewayBillStatus ?? {};
        const ewbNo = ewbStatus?.ewb_no ?? findRowField(item, ["ewbno", "ewb_eway_bill_number", "ewb_no"]);

        const rawDate    = inv?.soit_order_date   ?? inv?.created_date;
        const rawExpiry  = findRowField(item, ["validupto", "ewb_valid_upto", "ewb_expiry_date"]);

        /* NIC allows cancelling an e-way bill within 24 hours of generation */
        const ewbDate = findRowField(item, ["ewbdate", "ewb_date"]);
        const isCancelled = ewbStatus?.status === "CANCELLED" ||
            [true, "true", 1].includes(ewbStatus?.is_cancelled ?? findRowField(item, ["is_cancelled"]));
        const isRejected = ewbStatus?.status === "REJECTED" ||
            [true, "true", 1].includes(ewbStatus?.is_rejected ?? findRowField(item, ["is_rejected"]));
        const cancelUntil = parseEwbDate(ewbDate)
            ? parseEwbDate(ewbDate).add(EWB_CANCEL_WINDOW_HOURS, "hours")
            : null;
        const canCancel = Boolean(ewbNo) && !isCancelled && !isRejected &&
            (!cancelUntil || moment().isBefore(cancelUntil));
        const rejectUntil = parseEwbDate(ewbDate)
            ? parseEwbDate(ewbDate).add(EWB_REJECT_WINDOW_HOURS, "hours")
            : null;
        const canReject = Boolean(ewbNo) && !isCancelled && !isRejected &&
            (!rejectUntil || moment().isBefore(rejectUntil));

        return {
            id: inv?.soit_id ?? item?.soit_id,
            soit_order_date: rawDate && moment(rawDate).isValid()
                ? moment(rawDate).format("MMM D, YYYY")
                : "--",
            soit_invoice_number: (
                <span className="fw_500 cursor-pointer">{invNo}</span>
            ),
            ewb_number: ewbNo ? (
                <span className="fw_500">{ewbNo}</span>
            ) : (
                <span className="text-[var(--placeholder-color)]">--</span>
            ),
            cmr_name:  inv?.cmr_name  ?? item?.cmr_name  ?? "--",
            cmr_gstin: findRowField(item, ["to_gstin"]) ?? "--",
            ewb_expiry_date: parseEwbDate(rawExpiry)
                ? parseEwbDate(rawExpiry).format("MMM D, YYYY")
                : "--",
            soit_total_amount: "₹ " + (inv?.soit_total_amount ?? item?.soit_total_amount ?? "0.00"),
            order_status: <EInvoiceStatusBadge status={ewbStatus?.status} />,
            soit_order_status: inv?.soit_order_status ?? item?.soit_order_status ?? "",
            /* used by the row menu / cancel popup */
            ewb_no: ewbNo,
            transaction_no: invNo,
            cancel_until: cancelUntil,
            can_cancel: canCancel,
            /* not offered for cancelled / already rejected bills, or after 72 hours */
            can_reject: canReject,
        };
    });

    const rowMenuItems = (rowData) => [
        ...(rowData?.can_cancel
            ? [{
                label: "Cancel e-Way Bill",
                icon: <XCircle size={14} strokeWidth={2} />,
                danger: true,
                onClick: () => setCancelRow(rowData),
            }]
            : []),
        ...(rowData?.can_reject
            ? [{
                label: "Reject e-Way Bill",
                icon: <Ban size={14} strokeWidth={2} />,
                danger: true,
                onClick: () => setRejectRow(rowData),
            }]
            : []),
    ];

    return (
        <InnerLayout
            permissionFn={permissionFn}
            module="sales_invoice"
            title="e-Way Bills"
        >
            {loading ? (
                <LoadingSpinner />
            ) : error ? (
                <></>
            ) : (
                <>
                    <DynamicTransactionTable
                        transaction="e-Way Bill"
                        transaction_type="sales_invoice"
                        setInvoiceItems={() => { }}
                        invoiceItems={[]}
                        options={[]}
                        orders={eWayBills}
                        setOrders={setEWayBills}
                        tableHeading1={tableHeading1}
                        is_checkbox_req={false}
                        onClickItemFn={(id) => navigate(`/view-e-way-bill/${id}`)}
                        list={false}
                        permissionFn={permissionFn}
                        module={"sales_invoice"}
                        path="sales-invoice"
                        reload={reload}
                        setReload={setReload}
                        loading={loading}
                        setLoading={setLoading}
                        deleteAction={() => { }}
                        label="e-Way Bill"
                        showFilter={showFilter}
                        filterFn={handleFilterClick}
                        setFilterData={setFilterData}
                        filterData={filterData}
                        paginationProps={paginationProps}
                        tableHeading={tableHeading}
                        tableBodyData={tableBodyData}
                        placeholder="Search (/)"
                        filterBy={filterby}
                        initialFilter={{
                            sort_by: "",
                            sort_order: "",
                            selectColumn: "soit_invoice_number",
                            from: "",
                            to: "",
                            inputValue: "",
                            orderStatus: "",
                        }}
                        handleSelect={() => { }}
                        transactionKey="soit"
                        actionsPermissions={{
                            edit: [],
                            delete: [],
                            print: [],
                            send: [],
                            clone: [],
                        }}
                        rowMenuItems={rowMenuItems}
                        menu={true}
                    />
                    {cancelRow && (
                        <CancelEWayBillModal
                            soitId={cancelRow.id}
                            ewbNo={cancelRow.ewb_no}
                            transactionNo={cancelRow.transaction_no}
                            cancelUntil={cancelRow.cancel_until}
                            onClose={() => setCancelRow(null)}
                            onCancelled={() => {
                                setCancelRow(null);
                                setReload((prev) => !prev);
                            }}
                        />
                    )}
                    {rejectRow && (
                        <RejectEWayBillModal
                            soitId={rejectRow.id}
                            ewbNo={rejectRow.ewb_no}
                            transactionNo={rejectRow.transaction_no}
                            onClose={() => setRejectRow(null)}
                            onRejected={() => {
                                setRejectRow(null);
                                setReload((prev) => !prev);
                            }}
                        />
                    )}
                </>
            )}
        </InnerLayout>
    );
};

export default EWayBillsTable;

const tableHeading = [
    {
        label: "DATE",
        value: "soit_order_date",
        display: true,
        column: true,
    },
    {
        label: "TRANSACTION #",
        value: "soit_invoice_number",
        display: true,
        column: true,
    },
    {
        label: "EWB #",
        value: "ewb_number",
        display: true,
        column: true,
    },
    {
        label: "CUSTOMER NAME",
        value: "cmr_name",
        display: true,
        column: true,
    },
    {
        label: "CUSTOMER GSTIN",
        value: "cmr_gstin",
        display: true,
        column: true,
    },
    {
        label: "VALID UPTO",
        value: "ewb_expiry_date",
        display: true,
        column: true,
    },
    {
        label: "TOTAL",
        value: "soit_total_amount",
        display: true,
        column: true,
        className: "justify-end pe-5",
    },
    {
        label: "STATUS",
        value: "order_status",
        display: true,
        column: true,
    },
    {
        label: "",
        value: "actions",
        column: false,
    },
];

const tableHeading1 = [
    { label: "PRODUCT NAME", value: "item_name" },
    { label: "GENERIC NAME", value: "item_generic_name" },
    { label: "ITEM CODE", value: "item_code" },
    { label: "UOM", value: "item_uom" },
    { label: "PRICE (₹)", value: "item_unit_price" },
    { label: "QTY", value: "item_quantity" },
    { label: "TOTAL (₹)", value: "item_total_amount" },
];
